/**
 * useReferrals Hook
 *
 * Custom React hook for managing referrals data with filtering, pagination,
 * loading states, and error handling. Integrates with the unified ApiService
 * to fetch and manage referral information through the ReferralDomain.
 *
 * @module hooks/useReferrals
 */

import { useState, useEffect, useCallback } from 'react';
import type { Referral, ReferralsFilterState, UseReferralsReturn } from '@/types/dashboard';
import { apiService } from '@/services/ApiService';
import { ReferralApiError } from '@/services/domains/ReferralDomain';
import type { Referral as ApiReferral, ListReferralsRequest } from '@/services/domains/ReferralDomain';
import { getLogger } from '@/services/shared/LoggerFactory';
import { imageService } from '@/services/ImageService';
import { useMfaStore } from '@/stores/mfaStore';

/**
 * Hook for managing referrals with filtering and pagination
 *
 * @param filterState - Current filter and pagination state
 * @returns Object containing referrals data, pagination info, loading state, error state, and refresh function
 *
 * @example
 * ```typescript
 * const ReferralsComponent = () => {
 *   const [filterState, setFilterState] = useState({
 *     searchTerm: '',
 *     statusFilter: 'all',
 *     sortBy: 'date',
 *     sortOrder: 'desc',
 *     page: 1,
 *     pageSize: 10
 *   });
 *
 *   const { referrals, pagination, loading, error, refresh } = useReferrals(filterState);
 *
 *   if (loading) return <div>Loading referrals...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *
 *   return (
 *     <div>
 *       {referrals.map(referral => (
 *         <div key={referral.id}>{referral.patientName}</div>
 *       ))}
 *       <div>Page {pagination.page} of {pagination.totalPages}</div>
 *     </div>
 *   );
 * };
 * ```
 */
/**
 * Map API status to dashboard status
 */
const mapApiStatusToDashboard = (apiStatus: import('@/services/domains/ReferralDomain').ReferralStatus): import('@/types/dashboard').ReferralStatus => {
  // Handle the mapping between different status enums
  switch (apiStatus) {
    case 'completed':
      return 'complete';
    default:
      return apiStatus as import('@/types/dashboard').ReferralStatus;
  }
};

/**
 * Map dashboard status to API status
 */
const mapDashboardStatusToApi = (dashboardStatus: import('@/types/dashboard').ReferralStatus): import('@/services/domains/ReferralDomain').ReferralStatus => {
  switch (dashboardStatus) {
    case 'complete':
      return 'completed';
    case 'scheduled':
    case 'no_show':
    case 'awaiting_mealplan':
      // These statuses don't exist in the API enum, map to closest equivalent
      return 'claimed';
    default:
      return dashboardStatus as import('@/services/domains/ReferralDomain').ReferralStatus;
  }
};

/**
 * Transform API referral (snake_case) to dashboard referral (camelCase)
 */
const transformApiReferralToDashboard = (apiReferral: ApiReferral): Referral => {
  return {
    id: apiReferral.id,
    referralToken: apiReferral.referral_token,
    referringPhysicianId: apiReferral.referring_physician_id,
    dietitianId: apiReferral.dietitian_id || '',
    patientName: apiReferral.patient_name || '',
    dateOfBirth: apiReferral.date_of_birth || '',
    patientIdNumber: apiReferral.patient_id_number || '',
    patientIdType: apiReferral.patient_id_type || '',
    phone: apiReferral.phone || '',
    email: apiReferral.email || '',
    conditions: apiReferral.conditions || [],
    additionalNotes: apiReferral.additional_notes,
    status: mapApiStatusToDashboard(apiReferral.status),
    claimedByUserId: apiReferral.claimed_by_user_id,
    claimedAt: apiReferral.claimed_at,
    expiresAt: apiReferral.expires_at,
    createdAt: apiReferral.created_at,
    updatedAt: apiReferral.updated_at,
    referringPhysicianName: apiReferral.referring_physician_name || '',
    referringPhysicianEmail: apiReferral.referring_physician_email || '',
    referringPhysicianRole: apiReferral.referring_physician_role,
    dietitianName: apiReferral.dietitian_name || '',
    dietitianEmail: apiReferral.dietitian_email || '',
  };
};

/**
 * Transform filter state to API request parameters
 */
const transformFilterStateToApiRequest = (filterState: ReferralsFilterState): ListReferralsRequest => {
  const request: ListReferralsRequest = {};

  if (filterState.statusFilter && filterState.statusFilter !== 'all') {
    request.status = mapDashboardStatusToApi(filterState.statusFilter);
  }

  // Convert page-based pagination to offset-based
  request.limit = filterState.pageSize;
  request.offset = (filterState.page - 1) * filterState.pageSize;

  // Map sort parameters
  if (filterState.sortBy === 'date') {
    request.sortBy = 'createdAt';
  }
  request.sortOrder = filterState.sortOrder;

  return request;
};

/**
 * Get user-friendly error message from ReferralApiError
 */
const getUserFriendlyErrorMessage = (error: unknown): string => {
  if (error instanceof ReferralApiError) {
    switch (error.status) {
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You do not have permission to access this resource.';
      case 404:
        return 'The requested resource was not found.';
      case 408:
        return 'Request timed out. Please try again.';
      case 500:
      case 502:
      case 503:
      case 504:
        return 'A server error occurred. Please try again later.';
      default:
        return error.message || 'An unexpected error occurred.';
    }
  }

  if (error instanceof Error) {
    const message =
      typeof (error as unknown as { message?: unknown })?.message === 'string'
        ? (error as unknown as { message: string }).message
        : '';
    if (message.includes('Failed to fetch') || message.includes('Network')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    return message || 'An unexpected error occurred.';
  }

  return 'An unexpected error occurred.';
};

/**
 * Check if error is an authentication error
 */
const isAuthError = (error: unknown): boolean => {
  return error instanceof ReferralApiError && error.status === 401;
};

/**
 * Check if error is an MFA required error
 */
const isMfaError = (error: unknown): boolean => {
  return error instanceof ReferralApiError && error.status === 403 && error.code === 'MFA_REQUIRED';
};

export const useReferrals = (filterState: ReferralsFilterState): UseReferralsReturn => {
  const logger = getLogger();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string | null>>({});
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check MFA state - don't make API calls if MFA is required
  const mfaRequired = useMfaStore((state) => state.mfaRequired);

  /**
   * Fetch referrals from API with current filter state
   */
  const fetchReferrals = useCallback(async () => {
    // Skip fetching if MFA is required
    if (mfaRequired) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const apiRequest = transformFilterStateToApiRequest(filterState);
      const response = await apiService.referral.listReferrals(apiRequest);

      // Transform API referrals to dashboard format
      const transformedReferrals = response.referrals.map(transformApiReferralToDashboard);
      setReferrals(transformedReferrals);

      // Fetch photo URLs for all relevant user IDs from referrals
      try {
        const photoUrlsMap = await imageService.preloadReferralPhotos(response.referrals);
        setPhotoUrls(photoUrlsMap);
      } catch (photoError) {
        logger.warn('Failed to fetch photo URLs:', photoError);
        // Don't fail the entire operation if photo fetching fails
        setPhotoUrls({});
      }

      // Use page-based pagination from response
      setPagination({
        total: response.total,
        page: response.page,
        pageSize: response.per_page,
        totalPages: response.total_pages,
      });

    } catch (err) {
      // Don't set error for MFA errors - redirect will handle it
      if (isMfaError(err)) {
        setLoading(false);
        return;
      }

      const errorMessage = getUserFriendlyErrorMessage(err);
      setError(errorMessage);

      // Log the actual error for debugging (but not MFA errors)
      logger.error('Referrals fetch failed:', err);

      // Clear referrals on error to avoid showing stale data
      setReferrals([]);
      setPagination({
        total: 0,
        page: 1,
        pageSize: filterState.pageSize,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [filterState, mfaRequired]);

  /**
   * Refresh referrals data
   */
  const refresh = useCallback(async () => {
    await fetchReferrals();
  }, [fetchReferrals]);

  /**
   * Fetch referrals when filter state changes
   */
  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  /**
   * Handle authentication errors
   */
  useEffect(() => {
    if (error && isAuthError(error)) {
      // In a real app, this would trigger a redirect to login
      logger.warn('Authentication error detected, user should be redirected to login');
    }
  }, [error]);

  /**
   * Reset to first page when search term or status filter changes
   * This is handled in the useReferralsFilter hook, but we can also
   * validate here to ensure consistency
   */
  useEffect(() => {
    if (filterState.page > pagination.totalPages && pagination.totalPages > 0) {
      logger.warn('Current page exceeds total pages, consider resetting to page 1');
    }
  }, [filterState.page, pagination.totalPages]);

  return {
    referrals,
    photoUrls,
    pagination,
    loading,
    error,
    refresh,
  };
};

export default useReferrals;